/*
* Report pages: yearly cash flow
*/
import * as _ from 'lodash';
import * as React from 'react';

import { Formatters } from '../../../common/formatters';
import * as ReportComponents from '../report-components';

export const Render = ({ registerFunc, refFunc, scrollToFunc, maxColumnsPerTable = 0, pageTitle = '', className = '', maxTableRowsPerPage = 0, pageConfiguration, pageCount, reportData, appText, ...rest }) => {
    const sectionName = ReportComponents.ReportSectionNames.yearlyCashFlow;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageHeaderFooter, sectionName, registerFunc, refFunc, scrollToFunc);
    const getTableRowKey = (id, secName, tabName = 'data') => ReportComponents.getRowKey(id, secName, tabName);

    const { formData, association, inventory, currentOrder, currentOrderChart, percentFunded, yearlySummaryData } = reportData;

    // Configuration
    const maxYearlyCaptionLenWrap = 10;

    const chunkSize = (maxTableRowsPerPage > 0) ? maxTableRowsPerPage : _.get(pageConfiguration, 'maxTableRowsPerPage', 15);
    const chunkColumnSize = (maxColumnsPerTable > 0) ? maxColumnsPerTable : _.get(pageConfiguration, 'maxColumnsPerTable', 5);

    const transformDataItems = (dataItems: any[]) => {
        let results: any[] = _(dataItems)
            .orderBy('year')
            .map((item, i) => {
                const transformedItem = {
                    year: _.get(item, 'year'),
                    startingBalance: Math.round(_.get(item, 'r_sb')),
                    interestEarnings: Math.round(_.get(item, 'r_ii')),
                    specialAssessments: Math.round(_.get(item, 'r_sa')),
                    fundsAvailable: Math.round(_.get(item, 'r_sb')) + Math.round(_.get(item, 'r_mrcIncome')) + Math.round(_.get(item, 'r_ii')), // special assessment is included in mrcIncome, so not included here
                    reserveExpenditures: Math.round(_.get(item, 'r_re')),
                    endingBalance: Math.round(_.get(item, 'r_erb')),
                    reserveIncome: Math.round(_.get(item, 'r_mrcIncome')) - Math.round(_.get(item, 'r_sa')), // special assessment is included in r_mrcIncome but should not be here
                };

                return _.merge({}, item, transformedItem);
            })
            .value();

        return results;
    };

    const buildRows = (dataItems: any[], tableName: string = 'tbl') => {
        const cellValue = (v: any, i: number) => (<td key={i} className="text-right pdf-page-yearly-cashflow-table-cell-value"><ReportComponents.Currency value={v} decimals={0} /></td>);
        const cellStrong = (s: string, i: number) => (<td key={i} className="text-left pdf-page-yearly-cashflow-table-row-header-strong">{s}</td>);
        const cellItalic = (s: string, i: number) => (<td key={i} className="text-left pdf-page-yearly-cashflow-table-row-header-italic">{s}</td>);

        const rowDefs = [
            { caption: appText('page.yearly-cash-flow.grid-column-headers.starting-balance'), itemField: 'startingBalance', offset: false },
            { caption: appText('page.yearly-cash-flow.grid-column-headers.reserve-income'), itemField: 'reserveIncome', offset: true },
            { caption: appText('page.yearly-cash-flow.grid-column-headers.interest-earnings'), itemField: 'interestEarnings', offset: true },
            { caption: appText('page.yearly-cash-flow.grid-column-headers.special-assessments'), itemField: 'specialAssessments', offset: true },
            { caption: appText('page.yearly-cash-flow.grid-column-headers.funds-available'), itemField: 'fundsAvailable', offset: false },
            { caption: appText('page.yearly-cash-flow.grid-column-headers.reserve-expenditures'), itemField: 'reserveExpenditures', offset: false },
            { caption: appText('page.yearly-cash-flow.grid-column-headers.ending-balance'), itemField: 'endingBalance', offset: false, className: 'pdf-page-yearly-cashflow-table-footer-row' },
        ];

        const rows = _(rowDefs)
            .map((rowDefItem, rowDefInd, rowDefsArr) => {
                const rowDefItemFieldName = Formatters.getStr(rowDefItem, 'itemField');
                const rowDefItemCaption = Formatters.getStr(rowDefItem, 'caption');
                const rowDefItemClassName = Formatters.getStr(rowDefItem, 'className');

                const yearColumns = _(dataItems)
                    .map((item, itemInd, arr) => {
                        const itemFieldValue = Formatters.getInt(item, rowDefItemFieldName);

                        return cellValue(itemFieldValue, itemInd);
                    })
                    .value();

                const firstCell = Formatters.getBool(rowDefItem, 'offset') ? cellItalic(rowDefItemCaption, rowDefInd) : cellStrong(rowDefItemCaption, rowDefInd);

                return (
                    <tr key={getTableRowKey(`${rowDefItemFieldName}-${rowDefInd}`, sectionName, `${tableName}-ycf-row`)} className={rowDefItemClassName}>
                        {firstCell}
                        {yearColumns}
                    </tr>
                );
            })
            .value();

        return rows;
    };

    const buildHeaderRow = (dataItems: any[], tableName: string = 'tbl') => {
        const yearColumns = _(dataItems)
            .map((item, ind, arr) => {
                const itemYear = Formatters.getInt(item, 'year');

                return (
                    <th className="pdf-page-yearly-cashflow-table-header-column-year-num text-right" key={getTableRowKey(`th-year-${itemYear}-${ind}`, sectionName, tableName)}>{itemYear}</th>
                );
            })
            .value();

        const row = (
            <tr className="pdf-page-yearly-cashflow-table-header-row">
                <th className="text-left pdf-page-yearly-cashflow-table-header-column-year-label">{appText('page.yearly-cash-flow.grid-column-headers.year')}</th>
                {yearColumns}
            </tr>
        );

        return row;
    };

    // CHUNK DATA TO TABLES
    const yearlySummaryDataCorrected = _.take(yearlySummaryData, Formatters.getInt(formData, 'calculateYears'));
    const tables = _(yearlySummaryDataCorrected)
        .thru(transformDataItems)
        .chunk(chunkColumnSize)
        .map((chunkItems, chunkInd, chunkInputArr) => {
            const headRow = buildHeaderRow(chunkItems, `br-yrc-table-year-${chunkInd}-th`);

            const rows = buildRows(chunkItems, `br-yrc-table-year-${chunkInd}-tr`);

            const chunkTable = (
                <ReportComponents.ReportTable
                    key={`br-yrc-table-year-${chunkInd}`}
                    theadRow={headRow}
                    tbodyRows={rows}
                    className="pdf-page-yearly-cashflow-table-group"
                    classNameTable="pdf-data-table-yearly-cashflow"
                />
            );

            return chunkTable;
        })
        .value();

    // CHUNK TABLES TO PAGES
    const pages = _(tables)
        .chunk(chunkSize)
        .map((chunkItems, chunkInd, chunkInputArr) => {
            const page = (
                <ReportPageWithRegister
                    className={className}
                    footerData={reportData}
                    key={chunkInd}
                    pageIndex={chunkInd}
                    showFooter={true}
                    title={pageTitle}
                    totalPageCount={pageCount}
                >
                    {chunkItems}
                </ReportPageWithRegister>
            );

            return page;
        })
        .value();

    return (
        <div>
            {pages}
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-yearly-cash-flow.tsx